import type { FormEvent } from 'react'
import {
  AssistedSaleSearchbar,
  Searchbar,
  ServicesSearchbar
} from '@leroy-merlin-br/backpack-header'
import { liteClient as search } from 'algoliasearch/lite'

import { AlgoliaConfigProps } from 'scripts/react-components/entry-key/routes/routes'
import { useAnalyticsTags } from 'scripts/react-components/entry-key/hooks/useAnalyticsTags'

import { useUserResources } from 'shared/hooks'
import { insightsConfig } from 'shared/contexts/AlgoliaInstantSearch/lib'

export type SearchbarByContextProps = {
  algolia: AlgoliaConfigProps
  isServicesSearchbarEnabled: boolean
  isAlgoliaAssistedSaleEnabled?: boolean
}

export function SearchbarByContext(props: Readonly<SearchbarByContextProps>) {
  const { algolia, isServicesSearchbarEnabled, isAlgoliaAssistedSaleEnabled } =
    props
  const {
    indexName,
    appId,
    apiKey,
    retailMediaIndexName,
    serviceIndexName,
    assistedSaleProductIndexName,
    assistedSaleServiceIndexName
  } = algolia
  const searchClient = search(appId, apiKey)

  const {
    userResources,
    userControls: { isAssistedSale }
  } = useUserResources()
  const analyticsTags = useAnalyticsTags()
  const storeSlug = userResources?.storeSlug ?? undefined

  const isAlgoliaAssistedSale = isAssistedSale && !!isAlgoliaAssistedSaleEnabled

  const shouldShowAssistedSaleSearchbar =
    isAssistedSale && !isAlgoliaAssistedSale

  const handleAssistedSaleSubmitCapture = (event: FormEvent) => {
    const form = event.target as HTMLFormElement
    const formData = new FormData(form)
    const term = String(formData.get('assisted-sale-input') || '').trim()

    if (!term) {
      return
    }

    const params = new URLSearchParams({
      term,
      searchTerm: term,
      searchType: 'default'
    })

    event.preventDefault()
    event.stopPropagation()
    window.location.href = `/search?${params.toString()}`
  }

  if (shouldShowAssistedSaleSearchbar) {
    return (
      <div onSubmitCapture={handleAssistedSaleSubmitCapture}>
        <AssistedSaleSearchbar />
      </div>
    )
  }

  const shouldShowServices = isAlgoliaAssistedSale || isServicesSearchbarEnabled

  const activeProductIndexName = isAlgoliaAssistedSale
    ? assistedSaleProductIndexName
    : indexName

  const activeServiceIndexName = isAlgoliaAssistedSale
    ? assistedSaleServiceIndexName
    : serviceIndexName

  const algoliaConfig = {
    insightsClient: insightsConfig.insightsClient,
    searchClient,
    indexName: activeProductIndexName,
    retailMediaIndexName,
    productServicesIndexName: activeServiceIndexName,
    queries: [
      {
        params: {
          analyticsTags,
          ...(isAlgoliaAssistedSale && {
            filters: `store_${storeSlug}.isVisibleOnRegion:true`
          })
        },
        indexName: activeProductIndexName
      }
    ]
  }

  if (shouldShowServices) {
    return (
      <ServicesSearchbar
        maxWidth="6xl"
        algolia={algoliaConfig}
        storeSlug={storeSlug}
      />
    )
  }

  return <Searchbar algolia={algoliaConfig} />
}
